import axios from "axios";
import { axiosInstance } from "./index";

export interface EngagementTermsItem {
	id: string;
	name: string;
	dueDate: string;
	sortOrder: number;
}

export interface EngagementTermsDeliverable {
	id: string;
	name: string;
	deliverableTypes: string[];
	dueDate: string;
	liveDate: string | null;
	sortOrder: number;
	items: EngagementTermsItem[];
}

export interface CampaignEngagementTerms {
	id: string;
	campaignId: string;
	marketBlockId: string;
	effectiveStart: string;
	effectiveEnd: string;
	liveDate: string | null;
	paymentTerms: string;
	creatorFeeAmount: number | null;
	creatorFeeCurrency: string;
	exclusivityEnabled: boolean;
	exclusivityPeriod: string | null;
	exclusivityCategory: string | null;
	exclusivityBrands: string[];
	deliverables: EngagementTermsDeliverable[];
	updatedAt: string;
	scope: "market" | "creator";
	creatorAssignmentId: string | null;
	sourceTermsId: string | null;
	customizedFields: EngagementTermsCustomizationField[];
	versionNumber: number;
	responseStatus: EngagementTermsResponseStatus;
	presentedAt: string | null;
	respondedAt: string | null;
	responseReason: string | null;
	changeSummary: string | null;
	respondedByName: string | null;
}

export type EngagementTermsResponseStatus = "draft" | "pending" | "accepted" | "declined" | "superseded";

export type EngagementTermsCustomizationField =
	| "effectiveDates"
	| "currency"
	| "paymentTerms"
	| "deliverables"
	| "exclusivity";

export interface SaveCampaignEngagementTerms {
	effectiveStart: string;
	effectiveEnd: string;
	liveDate?: string | null;
	paymentTerms: string;
	creatorFeeAmount?: number | null;
	creatorFeeCurrency: string;
	exclusivityEnabled: boolean;
	exclusivityPeriod: string | null;
	exclusivityCategory: string | null;
	exclusivityBrands: string[];
	deliverables: Array<{
		name: string;
		deliverableTypes: string[];
		dueDate: string;
		liveDate: string | null;
		items: Array<{ name: string; dueDate: string }>;
	}>;
	customizedFields?: EngagementTermsCustomizationField[];
}

export interface EngagementCreator {
	assignmentId: string;
	creatorId: string;
	name: string;
	email: string | null;
	finalRate: number | null;
	avatarUrl: string | null;
	handle: string | null;
	invitationStatus: "not_sent" | "revision" | "sent" | "accepted";
	isCustomized: boolean;
	isReturningCreator: boolean;
	latestVersionNumber: number | null;
}

const path = (campaignId: string, marketBlockId: string) =>
	`/campaigns/${campaignId}/markets/${marketBlockId}/engagement-terms`;
const creatorPath = (campaignId: string, marketBlockId: string, creatorAssignmentId: string) =>
	`/campaigns/${campaignId}/markets/${marketBlockId}/creators/${creatorAssignmentId}/engagement-terms`;

export const engagementTermsAPI = {
	async hasCampaignTerms(campaignId: string): Promise<boolean> {
		const response = await axiosInstance.get<{ hasEngagementTerms: boolean }>(
			`/campaigns/${campaignId}/engagement-terms/availability`,
		);
		return response.data.hasEngagementTerms;
	},
	async getMarketCreators(campaignId: string, marketBlockId: string): Promise<EngagementCreator[]> {
		const response = await axiosInstance.get<EngagementCreator[]>(
			`/campaigns/${campaignId}/markets/${marketBlockId}/engagement-creators`,
		);
		return response.data;
	},
	async getMarketTerms(campaignId: string, marketBlockId: string): Promise<CampaignEngagementTerms | null> {
		try {
			const response = await axiosInstance.get<CampaignEngagementTerms>(path(campaignId, marketBlockId));
			return response.data;
		} catch (error) {
			if (axios.isAxiosError(error) && error.response?.status === 404) return null;
			throw error;
		}
	},
	async getCreatorTerms(
		campaignId: string,
		marketBlockId: string,
		creatorAssignmentId: string,
	): Promise<CampaignEngagementTerms | null> {
		try {
			const response = await axiosInstance.get<CampaignEngagementTerms>(
				creatorPath(campaignId, marketBlockId, creatorAssignmentId),
			);
			return response.data;
		} catch (error) {
			if (axios.isAxiosError(error) && error.response?.status === 404) return null;
			throw error;
		}
	},
	async getCreatorTermVersions(
		campaignId: string,
		marketBlockId: string,
		creatorAssignmentId: string,
	): Promise<CampaignEngagementTerms[]> {
		const response = await axiosInstance.get<{ versions: CampaignEngagementTerms[] }>(
			`${creatorPath(campaignId, marketBlockId, creatorAssignmentId)}/versions`,
		);
		return response.data.versions;
	},
	async issueCreatorTermVersion(
		campaignId: string,
		marketBlockId: string,
		creatorAssignmentId: string,
	): Promise<CampaignEngagementTerms> {
		const response = await axiosInstance.post<{ terms: CampaignEngagementTerms }>(
			`${creatorPath(campaignId, marketBlockId, creatorAssignmentId)}/versions`,
		);
		return response.data.terms;
	},

	async saveMarketTerms(
		campaignId: string,
		marketBlockId: string,
		input: SaveCampaignEngagementTerms,
	): Promise<CampaignEngagementTerms> {
		const response = await axiosInstance.put<{ terms: CampaignEngagementTerms }>(
			path(campaignId, marketBlockId),
			input,
		);
		return response.data.terms;
	},
	async saveCreatorTerms(
		campaignId: string,
		marketBlockId: string,
		creatorAssignmentId: string,
		input: SaveCampaignEngagementTerms,
	): Promise<CampaignEngagementTerms> {
		const response = await axiosInstance.put<{ terms: CampaignEngagementTerms }>(
			creatorPath(campaignId, marketBlockId, creatorAssignmentId),
			input,
		);
		return response.data.terms;
	},
};
